import "bootstrap-icons/font/bootstrap-icons.css";
import "bootstrap/dist/css/bootstrap.min.css";
import { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { NavLink, useNavigate, useLocation } from "react-router-dom";
import "../App.css";
import { logout } from "../store/authSlice.js";

const Navbar = () => {
  const [drawerOpen, setDrawerOpen] = useState(false);
  const user = useSelector((s) => s.auth.user);
  const cartCount = useSelector((s) => s.cart.items.length || 0);
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();

  // Close drawer on route change
  useEffect(() => { setDrawerOpen(false); }, [location.pathname]);

  // Prevent body scroll when drawer open
  useEffect(() => {
    document.body.style.overflow = drawerOpen ? "hidden" : "";
    return () => { document.body.style.overflow = ""; };
  }, [drawerOpen]);

  const getAvatarUrl = (u) => {
    if (u?.photoURL) return u.photoURL;
    const seed = u?.email ? u.email.split("@")[0] : "user";
    return `https://api.dicebear.com/7.x/fun-emoji/svg?seed=${seed}`;
  };

  const getInitial = (u) => {
    if (u?.name) return u.name.charAt(0).toUpperCase();
    if (u?.email) return u.email.charAt(0).toUpperCase();
    return "U";
  };

  const isGoogleUser = (u) => u?.photoURL && u.photoURL.includes("googleusercontent");

  const AvatarImg = ({ u, size, className, style, onClick }) => {
    const [failed, setFailed] = useState(false);
    const colors = ["#984A1F","#2D6A4F","#1565C0","#6A1B9A","#C62828"];
    const color = colors[(u?.email?.charCodeAt(0) || 0) % colors.length];
    return (
      <div className="user-avatar-wrap" style={{ width: size, height: size, flexShrink: 0, ...style }} onClick={onClick}>
        {failed ? (
          <div className={`user-avatar-initial ${className || ""}`}
            style={{ width: size, height: size, background: color, fontSize: size * 0.42, borderRadius: "50%",
              display: "flex", alignItems: "center", justifyContent: "center",
              color: "#fff", fontWeight: 700, userSelect: "none" }}>
            {getInitial(u)}
          </div>
        ) : (
          <img src={getAvatarUrl(u)} alt="avatar" className={`rounded-circle ${className || ""}`}
            style={{ width: size, height: size, objectFit: "cover" }}
            onError={() => setFailed(true)} />
        )}
        {isGoogleUser(u) && (
          <span className="user-avatar-g-badge" title="Signed in with Google">G</span>
        )}
      </div>
    );
  };

  const handleLogout = () => {
    dispatch(logout());
    navigate("/");
  };

  const navLinks = [
    { to: "/", label: "Home", icon: "house-door", end: true },
    { to: "/perfumes", label: "Collections", icon: "gem" },
    { to: "/about", label: "About Us", icon: "info-circle" },
    { to: "/contact", label: "Contact", icon: "telephone" },
  ];

  return (
    <>
      {/* ── TOP NAVBAR ── */}
      <nav className="navbar fixed-top navbar-professional animate-slide-in-down" style={{ zIndex: 1030 }}>
        <div className="container-fluid px-3 px-md-4">

          {/* Hamburger — mobile only */}
          <button
            className="btn d-lg-none p-1 me-2"
            style={{ border: "none", background: "transparent" }}
            onClick={() => setDrawerOpen(true)}
            aria-label="Open menu"
          >
            <i className="bi bi-list" style={{ fontSize: 26, color: "#2D3142" }}></i>
          </button>

          {/* Logo */}
          <NavLink to="/" className="navbar-brand d-flex align-items-center gap-2" style={{ textDecoration: "none" }}>
            <img src="/L.png" alt="Logo" style={{ width: 36, height: 36, borderRadius: 8, objectFit: "contain" }} />
            <div style={{ lineHeight: 1.1 }}>
              <div className="fw-bold text-dark" style={{ fontSize: "1.1rem" }}>AarnaSignatures</div>
              <small className="text-muted" style={{ fontSize: "0.65rem", letterSpacing: "0.3px" }}>PREMIUM FRAGRANCES</small>
            </div>
          </NavLink>

          {/* Desktop nav links */}
          <div className="d-none d-lg-flex align-items-center gap-4 ms-4">
            {navLinks.map((l) => (
              <NavLink key={l.to} to={l.to} end={l.end}
                className={({ isActive }) => `nav-link fw-semibold ${isActive ? "active text-primary" : "text-dark"}`}
                style={{ fontSize: "0.95rem" }}>
                <i className={`bi bi-${l.icon} me-1`}></i>{l.label}
              </NavLink>
            ))}
          </div>

          {/* Right actions */}
          <div className="d-flex align-items-center gap-2 ms-auto">
            {/* Cart */}
            <NavLink to="/cart" className="position-relative d-flex align-items-center justify-content-center"
              style={{ width: 38, height: 38, borderRadius: 10, backgroundColor: "rgba(152,74,31,0.1)", color: "#984A1F", fontSize: "1.1rem" }}>
              <i className="bi bi-bag-fill" style={{ color: "#984A1F" }}></i>
              {cartCount > 0 && (
                <span className="position-absolute top-0 start-100 translate-middle badge rounded-pill bg-danger"
                  style={{ fontSize: "0.6rem", minWidth: 16, height: 16, padding: "2px 4px" }}>
                  {cartCount}
                </span>
              )}
            </NavLink>

            {/* User — desktop dropdown */}
            {user ? (
              <div className="dropdown d-none d-lg-block">
                <button className="btn btn-business dropdown-toggle d-flex align-items-center gap-2"
                  type="button" data-bs-toggle="dropdown"
                  style={{ fontSize: 13, padding: "7px 14px", borderRadius: 10 }}>
                  <AvatarImg u={user} size={26} />
                  <span className="fw-medium">{user.name?.split(" ")[0] || user.email?.split("@")[0] || "User"}</span>
                </button>
                <ul className="dropdown-menu dropdown-menu-end shadow border-0 rounded-3" style={{ minWidth: 210, zIndex: 1040 }}>
                  <li className="dropdown-header border-bottom pb-2 mb-1">
                    <div className="d-flex align-items-center gap-2">
                      <AvatarImg u={user} size={30} />
                      <div>
                        <div className="fw-semibold small">{user.name || "User"}</div>
                        <div className="text-muted" style={{ fontSize: 11 }}>{user.email}</div>
                      </div>
                    </div>
                  </li>
                  {[
                    { to: "/profile", icon: "person-circle", label: "My Profile", color: "text-primary" },
                    { to: "/my-orders", icon: "bag-check", label: "My Orders", color: "text-success" },
                    { to: "/addresses", icon: "geo-alt", label: "My Addresses", color: "text-info" },
                  ].map((item) => (
                    <li key={item.to}>
                      <NavLink to={item.to} className="dropdown-item rounded-2 mx-1 small">
                        <i className={`bi bi-${item.icon} me-2 ${item.color}`}></i>{item.label}
                      </NavLink>
                    </li>
                  ))}
                  <li><hr className="dropdown-divider mx-2 my-1" /></li>
                  <li>
                    <button className="dropdown-item text-danger rounded-2 mx-1 small" onClick={handleLogout}>
                      <i className="bi bi-box-arrow-right me-2"></i>Sign Out
                    </button>
                  </li>
                </ul>
              </div>
            ) : (
              <NavLink to="/signup" className="btn btn-business d-none d-lg-flex align-items-center"
                style={{ padding: "7px 16px", fontSize: "0.85rem", borderRadius: 10 }}>
                Get Started
              </NavLink>
            )}

            {/* Avatar icon on mobile for logged-in user */}
            {user && (
              <AvatarImg u={user} size={32} className="d-lg-none"
                style={{ cursor: "pointer", border: "2px solid #eee", borderRadius: "50%" }}
                onClick={() => setDrawerOpen(true)} />
            )}
          </div>
        </div>
      </nav>

      {/* ── SIDE DRAWER OVERLAY ── */}
      {drawerOpen && (
        <div
          onClick={() => setDrawerOpen(false)}
          style={{
            position: "fixed", inset: 0, background: "rgba(0,0,0,0.45)",
            zIndex: 1040, transition: "opacity 0.3s",
          }}
        />
      )}

      {/* ── SIDE DRAWER ── */}
      <div style={{
        position: "fixed", top: 0, left: 0, height: "100%", width: 270,
        background: "#fff", zIndex: 1050, boxShadow: "4px 0 24px rgba(0,0,0,0.15)",
        transform: drawerOpen ? "translateX(0)" : "translateX(-100%)",
        transition: "transform 0.3s cubic-bezier(0.4,0,0.2,1)",
        display: "flex", flexDirection: "column", overflowY: "auto",
      }}>
        {/* Drawer header */}
        <div className="d-flex align-items-center justify-content-between px-4 py-3"
          style={{ borderBottom: "1px solid #f0f0f0", background: "#fff8f0" }}>
          <div className="d-flex align-items-center gap-2">
            <img src="/L.png" alt="Logo" style={{ width: 32, height: 32, borderRadius: 6 }} />
            <span className="fw-bold" style={{ fontSize: "1rem", color: "#2D3142" }}>AarnaSignatures</span>
          </div>
          <button className="btn p-1" onClick={() => setDrawerOpen(false)} style={{ border: "none", background: "transparent" }}>
            <i className="bi bi-x-lg" style={{ fontSize: 20, color: "#666" }}></i>
          </button>
        </div>

        {/* User info in drawer */}
        {user && (
          <div className="px-4 py-3 d-flex align-items-center gap-3" style={{ borderBottom: "1px solid #f0f0f0" }}>
            <AvatarImg u={user} size={44} style={{ border: "2px solid #eee", borderRadius: "50%" }} />
            <div className="min-w-0">
              <div className="fw-semibold text-truncate" style={{ fontSize: 14 }}>{user.name || "User"}</div>
              <div className="text-muted text-truncate" style={{ fontSize: 11 }}>{user.email}</div>
              {isGoogleUser(user) && (
                <div style={{ fontSize: 10, color: "#4285F4", fontWeight: 600, marginTop: 2 }}>
                  <span style={{ marginRight: 3 }}>🔵</span>Google Account
                </div>
              )}
            </div>
          </div>
        )}

        {/* Nav links */}
        <div className="px-3 py-3 flex-grow-1">
          <div className="small text-muted fw-semibold px-2 mb-2" style={{ fontSize: 11, letterSpacing: 1 }}>MENU</div>
          {navLinks.map((l) => (
            <NavLink key={l.to} to={l.to} end={l.end}
              className={({ isActive }) =>
                `d-flex align-items-center gap-3 px-3 py-2 rounded-3 mb-1 text-decoration-none fw-medium ${isActive ? "text-white" : "text-dark"}`
              }
              style={({ isActive }) => ({
                background: isActive ? "linear-gradient(135deg,#984A1F,#7a3b19)" : "transparent",
                fontSize: 14,
              })}>
              <i className={`bi bi-${l.icon}`} style={{ fontSize: 16, width: 20, textAlign: "center" }}></i>
              {l.label}
            </NavLink>
          ))}

          {user && (
            <>
              <div className="small text-muted fw-semibold px-2 mt-4 mb-2" style={{ fontSize: 11, letterSpacing: 1 }}>ACCOUNT</div>
              {[
                { to: "/profile", icon: "person-circle", label: "My Profile" },
                { to: "/my-orders", icon: "bag-check", label: "My Orders" },
                { to: "/addresses", icon: "geo-alt", label: "My Addresses" },
                { to: "/cart", icon: "cart3", label: `Cart${cartCount > 0 ? ` (${cartCount})` : ""}` },
              ].map((item) => (
                <NavLink key={item.to} to={item.to}
                  className={({ isActive }) =>
                    `d-flex align-items-center gap-3 px-3 py-2 rounded-3 mb-1 text-decoration-none fw-medium ${isActive ? "text-white" : "text-dark"}`
                  }
                  style={({ isActive }) => ({
                    background: isActive ? "linear-gradient(135deg,#984A1F,#7a3b19)" : "transparent",
                    fontSize: 14,
                  })}>
                  <i className={`bi bi-${item.icon}`} style={{ fontSize: 16, width: 20, textAlign: "center" }}></i>
                  {item.label}
                </NavLink>
              ))}
            </>
          )}
        </div>

        {/* Drawer footer */}
        <div className="px-3 py-3" style={{ borderTop: "1px solid #f0f0f0" }}>
          {user ? (
            <button className="btn w-100 rounded-3 d-flex align-items-center gap-2 justify-content-center"
              style={{ background: "#fff0f0", color: "#dc3545", border: "1px solid #fcc", fontSize: 14 }}
              onClick={handleLogout}>
              <i className="bi bi-box-arrow-right"></i>Sign Out
            </button>
          ) : (
            <NavLink to="/signup" className="btn btn-business w-100 rounded-3" style={{ fontSize: 14 }}>
              <i className="bi bi-person-plus me-2"></i>Get Started
            </NavLink>
          )}
        </div>
      </div>
    </>
  );
};

export default Navbar;
